import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "../i18n";
import { api } from "../api";

const IMG_HERO =
  "https://images.unsplash.com/photo-1660496685662-b635f574f395?auto=format&fit=crop&w=2000&q=85";
const IMG_IMMERSION =
  "https://images.unsplash.com/photo-1664134973092-032088dd909b?auto=format&fit=crop&w=1600&q=85";
const IMG_STUDENTS =
  "https://images.unsplash.com/photo-1770102412124-3d01ecc81c81?auto=format&fit=crop&w=1200&q=85";
const IMG_TEACHERS =
  "https://images.pexels.com/photos/8500673/pexels-photo-8500673.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800";
const IMG_PARTNERSHIPS =
  "https://images.unsplash.com/photo-1507537297725-24a1c029d3ca?auto=format&fit=crop&w=1200&q=85";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Format an ISO datetime as a compact "Wed 14:00" in the user's locale.
 * If the slot is today, returns "Today 14:00"; if tomorrow, "Tomorrow 14:00".
 */
function formatSlot(iso, lang) {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOf(d) - startOf(now)) / 86400000);
  const time = d.toLocaleTimeString(lang === "es" ? "es-ES" : "en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  if (dayDiff === 0) return `${lang === "es" ? "Hoy" : "Today"} ${time}`;
  if (dayDiff === 1) return `${lang === "es" ? "Mañana" : "Tomorrow"} ${time}`;
  const weekday = d.toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { weekday: "short" });
  return `${weekday} ${time}`;
}

const Door = ({ image, label, index, onClick, testid, quote }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      role="link"
      tabIndex={0}
      data-testid={testid}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      className="relative flex-1 min-h-[120px] rounded-2xl md:rounded-3xl overflow-hidden cursor-pointer group select-none"
      whileHover={{ y: -2 }}
      transition={{ duration: 0.5, ease: EASE }}
    >
      <motion.img
        src={image}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        animate={{ scale: hovered ? 1.06 : 1 }}
        transition={{ duration: 1.2, ease: EASE }}
        draggable={false}
      />

      {/* Subtle wash that deepens on hover so the serif label reads on bright photos */}
      <motion.div
        className="absolute inset-0"
        animate={{ backgroundColor: hovered ? "rgba(28,28,28,0.42)" : "rgba(28,28,28,0.2)" }}
        transition={{ duration: 0.5 }}
      />

      {/* Idle micro-label, top-left */}
      <div className="absolute top-4 left-5 md:top-5 md:left-6 text-white/90 text-[10px] tracking-[0.32em] uppercase font-medium">
        0{index}
      </div>

      {/* Centered massive serif label — fades down on hover when a quote is provided so the quote can take over the optical centre */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center px-6"
        animate={{ scale: hovered ? 1.04 : 1, opacity: quote && hovered ? 0.18 : 1 }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        <span
          className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-none tracking-tighter font-light text-center"
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}
        >
          {label}
        </span>
      </motion.div>

      {/* On-hover quote pull: a tasteful philosophy excerpt that fades in over the door (only when a quote prop is passed). */}
      {quote && (
        <motion.div
          aria-hidden={!hovered}
          className="absolute inset-0 flex items-center justify-center px-7 md:px-10 pointer-events-none"
          animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 6 }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          <p
            className="font-display italic text-white text-base md:text-lg leading-[1.45] tracking-tight text-center max-w-[22ch]"
            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.55)" }}
          >
            <span className="not-italic opacity-80 mr-1">“</span>
            {quote}
            <span className="not-italic opacity-80 ml-1">”</span>
          </p>
        </motion.div>
      )}

      {/* "Enter →" cue on hover, bottom-right */}
      <motion.div
        className="absolute bottom-4 right-5 md:bottom-5 md:right-6 text-white text-[10px] tracking-[0.32em] uppercase font-medium"
        animate={{ opacity: hovered ? 0.9 : 0 }}
        transition={{ duration: 0.4 }}
      >
        Enter →
      </motion.div>
    </motion.div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [heroHovered, setHeroHovered] = useState(false);
  const [nextSlot, setNextSlot] = useState(undefined); // undefined=loading, null=none, {start,end}=slot

  useEffect(() => {
    let cancelled = false;
    api.nextAvailableSlot()
      .then((d) => { if (!cancelled) setNextSlot(d?.next_slot ?? null); })
      .catch(() => { if (!cancelled) setNextSlot(null); });
    return () => { cancelled = true; };
  }, []);

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-16 md:pt-20"
      data-testid="home-bento-root"
    >
      <div className="px-4 md:px-8 pb-4 md:pb-8 max-w-[1800px] mx-auto">
        <div className="flex flex-col md:flex-row gap-4 md:gap-6 md:h-[calc(100dvh-7rem)]">
          {/* Dominant school hero — visual stage, not interactive */}
          <motion.div
            onMouseEnter={() => setHeroHovered(true)}
            onMouseLeave={() => setHeroHovered(false)}
            className="relative md:flex-[3] rounded-2xl md:rounded-3xl overflow-hidden h-[44dvh] md:h-auto"
            data-testid="home-school-hero"
          >
            <motion.img
              src={IMG_HERO}
              alt="Galician language school facade in sunlight"
              className="absolute inset-0 w-full h-full object-cover"
              animate={{ scale: heroHovered ? 1.025 : 1 }}
              transition={{ duration: 4, ease: "linear" }}
              draggable={false}
            />
            {/* Gentle bottom-up gradient so the meta line stays legible without darkening the picture */}
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent pointer-events-none" />

            {/* Live slot pill — top-right, links to /book */}
            {nextSlot !== undefined && (
              <motion.button
                type="button"
                onClick={() => navigate("/book")}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
                data-testid="home-hero-next-slot"
                className="absolute top-6 right-6 md:top-8 md:right-8 group flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/15 hover:bg-black/45 hover:border-white/30 transition-colors cursor-pointer"
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${nextSlot ? "bg-emerald-400" : "bg-white/45"} ${nextSlot ? "animate-pulse" : ""}`}
                />
                <span className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/90 font-medium">
                  {nextSlot
                    ? `${lang === "es" ? "Próxima clase" : "Next slot"} · ${formatSlot(nextSlot.start, lang)}`
                    : (lang === "es" ? "Reservar una clase" : "Book a lesson")}
                </span>
                <span className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white/95 transition-colors">
                  →
                </span>
              </motion.button>
            )}

            {/* Bottom meta — quiet, museum-style caption */}
            <div className="absolute left-6 md:left-10 bottom-6 md:bottom-10 text-white max-w-[60%]">
              <div className="text-[10px] tracking-[0.32em] uppercase opacity-90 font-medium">
                Galicia · Spain
              </div>
              <div className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-light mt-2 md:mt-3">
                {t("school")}
              </div>
              {/* Editorial teaser — a single italic line that hints at the philosophy below.
                  Hidden on the smallest screens so the bento art breathes on mobile. */}
              <p
                className="hidden md:block mt-5 md:mt-7 font-display italic text-white/85 text-base lg:text-lg leading-[1.45] tracking-tight max-w-md"
                style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
                data-testid="home-hero-teaser"
              >
                {lang === "es"
                  ? "La mayoría de los métodos fracasan porque confunden el mapa con el territorio."
                  : "Most language learning fails because it confuses the map for the territory."}
              </p>
            </div>

            {/* Subtle scroll cue → "Our philosophy" section below the bento */}
            <motion.a
              href="#philosophy"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("philosophy")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="home-hero-philosophy-cue"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
              className="hidden md:flex absolute right-8 bottom-8 group items-center gap-2 text-white/85 hover:text-white transition-colors"
            >
              <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                {lang === "es" ? "Nuestra filosofía" : "Our philosophy"}
              </span>
              <motion.span
                className="text-base"
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
              >
                ↓
              </motion.span>
            </motion.a>
          </motion.div>

          {/* Four vertical doors */}
          <div className="md:flex-[1] flex flex-col gap-4 md:gap-6 min-h-[60dvh] md:min-h-0">
            <Door
              image={IMG_IMMERSION}
              label={t("immersion")}
              index={1}
              onClick={() => navigate("/immersion")}
              testid="door-immersion"
            />
            <Door
              image={IMG_STUDENTS}
              label={t("students")}
              index={2}
              onClick={() => navigate("/students")}
              testid="door-students"
              quote={
                lang === "es"
                  ? "Comunica desde el primer día."
                  : "Communicate from day one."
              }
            />
            <Door
              image={IMG_TEACHERS}
              label={t("teachers")}
              index={3}
              onClick={() => navigate("/teachers")}
              testid="door-teachers"
            />
            <Door
              image={IMG_PARTNERSHIPS}
              label={t("partnerships")}
              index={4}
              onClick={() => navigate("/partnerships")}
              testid="door-partnerships"
            />
          </div>
        </div>
      </div>

      <PhilosophySection lang={lang} />
    </main>
  );
}

/**
 * Editorial "Our teaching philosophy" section.
 *
 * Long-form, museum-style: a header band, a lead intro with a pull quote,
 * five numbered chapters with their own eyebrow + headline, and a closing line.
 * Bilingual — content lives in PHILOSOPHY_CONTENT[lang]. Inline italics/bold
 * are written directly as JSX in each value so EN and ES can diverge naturally.
 */
function PhilosophySection({ lang }) {
  const c = PHILOSOPHY_CONTENT[lang === "es" ? "es" : "en"];
  return (
    <section
      id="philosophy"
      data-testid="home-philosophy-section"
      className="scroll-mt-24 bg-[#FAF9F6] border-t border-[#1C1C1C]/8 px-6 md:px-10 py-20 md:py-32"
    >
      {/* Header band */}
      <div className="max-w-6xl mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/55 mb-4">
          {c.eyebrow}
        </div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter font-light text-[#1C1C1C] max-w-3xl">
          {c.headline}
        </h2>
      </div>

      {/* Lead intro */}
      <div className="max-w-3xl mx-auto mt-14 md:mt-20">
        <p className="font-display text-xl md:text-2xl leading-[1.5] tracking-tight text-[#1C1C1C]/90">
          {c.lead}
        </p>

        {/* Pull quote */}
        <blockquote className="my-12 md:my-16 pl-6 md:pl-8 border-l-2 border-[#1C1C1C]">
          <p className="font-display text-2xl md:text-3xl leading-[1.25] tracking-tight italic text-[#1C1C1C]">
            {c.pullQuote}
          </p>
        </blockquote>

        <p className="text-[17px] leading-[1.75] text-[#1C1C1C]/80">
          {c.afterPullQuote}
        </p>
      </div>

      {/* Chapter 01 */}
      <Chapter num="01" title={c.ch1.title} chapterLabel={c.chapterLabel}>
        {c.ch1.body}
      </Chapter>

      {/* Chapter 02 */}
      <Chapter num="02" title={c.ch2.title} chapterLabel={c.chapterLabel}>
        <p>{c.ch2.intro}</p>
        <ul className="space-y-3 my-2 not-prose">
          {c.ch2.bullets.map((line, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="mt-2 inline-block w-6 h-px bg-[#1C1C1C]/40 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <p>{c.ch2.outro}</p>
      </Chapter>

      {/* Chapter 03 */}
      <Chapter num="03" title={c.ch3.title} chapterLabel={c.chapterLabel}>
        {c.ch3.body}
      </Chapter>

      {/* Chapter 04 — with feedback loop */}
      <Chapter num="04" title={c.ch4.title} chapterLabel={c.chapterLabel}>
        {c.ch4.beforeLoop}
        <div className="not-prose my-8 grid sm:grid-cols-3 gap-4 sm:gap-3">
          {c.ch4.loop.map((s, i) => (
            <div key={i} className="relative bg-white border border-[#1C1C1C]/10 rounded-2xl p-5">
              <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/55 mb-2">
                {c.stepLabel} 0{i + 1}
              </div>
              <div className="font-display text-lg leading-tight text-[#1C1C1C] mb-2">
                {s.label}
              </div>
              <p className="text-[13px] leading-[1.6] text-[#1C1C1C]/70">{s.body}</p>
              {i < 2 && (
                <div className="hidden sm:block absolute top-1/2 -right-2 -translate-y-1/2 text-[#1C1C1C]/30 text-sm">→</div>
              )}
            </div>
          ))}
        </div>
        {c.ch4.afterLoop}
      </Chapter>

      {/* Chapter 05 */}
      <Chapter num="05" title={c.ch5.title} chapterLabel={c.chapterLabel}>
        {c.ch5.body}
      </Chapter>

      {/* Closing tagline */}
      <div className="max-w-3xl mx-auto mt-20 md:mt-28 pt-12 border-t border-[#1C1C1C]/10">
        <p className="font-display text-2xl md:text-3xl leading-[1.3] tracking-tight text-[#1C1C1C]">
          {c.closing}
        </p>
        <div className="mt-10 md:mt-12">
          <Link
            to="/book"
            data-testid="philosophy-book-cta"
            className="inline-flex items-center gap-3 px-7 py-4 bg-[#1C1C1C] text-[#FAF9F6] rounded-full text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
          >
            {c.bookCta}
            <span className="text-base">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

/** A numbered chapter with eyebrow + headline + flowing body. */
function Chapter({ num, title, chapterLabel = "Chapter", children }) {
  return (
    <article className="max-w-3xl mx-auto mt-16 md:mt-24">
      <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/45 mb-3">
        {chapterLabel} {num}
      </div>
      <h3 className="font-display text-2xl md:text-3xl leading-[1.15] tracking-tight font-light text-[#1C1C1C] mb-6 md:mb-8">
        {title}
      </h3>
      <div className="space-y-5 text-[17px] leading-[1.75] text-[#1C1C1C]/80">
        {children}
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Philosophy copy (EN + ES). Inline <em>/<strong> stay in the values so     */
/*  each language can be edited / shipped independently.                      */
/* -------------------------------------------------------------------------- */

const PHILOSOPHY_CONTENT = {
  en: {
    eyebrow: "Our teaching philosophy",
    chapterLabel: "Chapter",
    stepLabel: "Step",
    headline: "Beyond the app, beyond the classroom.",
    lead: (
      <>
        You have likely tried to learn a language before. Perhaps in a physical
        classroom, battling evening traffic and a fixed schedule, only to find
        that the textbook bore little relation to a fast-moving conversation in
        a Madrid bar. Or perhaps you have tried the apps — convenient,
        certainly, but ultimately unsatisfying. A dopamine hit of flashcards
        and isolated sentences that rarely survive a real-world encounter.
      </>
    ),
    pullQuote: "There is a reason for that frustration. And it is not you.",
    afterPullQuote: (
      <>
        Most language learning fails because it confuses the map for the
        territory. It treats Spanish as a subject to be memorised — like
        mathematics or music notation — rather than a tool to be used. But
        human languages are not form; they are function. You do not speak to
        conjugate perfectly; you speak to achieve a goal: to close a deal, to
        make a friend, to navigate an unfamiliar city.
      </>
    ),
    ch1: {
      title: "The communicative principle.",
      body: (
        <>
          <p>
            Our Director of Studies comes from the tradition of{" "}
            <em>Communicative Language Teaching</em> (CLT). In practice, this
            means one non-negotiable rule: language is never taught in a vacuum.
            Every lesson, every exercise, every correction is rooted in
            real-world communication.
          </p>
          <p>
            We do not start with grammar tables. We start with a need — a
            genuine communicative gap — and the grammar emerges naturally as
            the tool to fill it. That cognitive "hook" is what makes learning
            memorable. It is the difference between studying the rules of
            swimming and being pushed into the water.
          </p>
        </>
      ),
    },
    ch2: {
      title: "Why our virtual classrooms are different.",
      intro:
        "You will find many private tutors on Zoom or Skype. They are fine tutors, but they are constrained by generic tools not built for pedagogy. We built our own virtual learning environment from the ground up.",
      bullets: [
        "Screen sharing, video conferencing, chat, and a virtual whiteboard, all integrated.",
        "Session recordings are automatically available to students for review.",
        "You control the process. No third-party apps. No data leaving our ecosystem.",
      ],
      outro:
        "This allows us to recreate the dynamism of a physical communicative classroom — paired tasks, real-time feedback, shared digital whiteboards — without the constraints of travel or fixed locations. And unlike an app, our classes happen between human beings, negotiating meaning in real time.",
    },
    ch3: {
      title: "One-to-one (and small groups).",
      body: (
        <>
          <p>
            Most of our teaching is one-to-one. Why? Because communication is
            not a broadcast; it is a dialogue. A private setting allows us to
            tailor every communicative situation to your professional or
            personal context. If you are a lawyer, we negotiate a contract. If
            you are in logistics, we resolve a shipping delay. If you are
            preparing for a move to Spain, we role-play calling a plumber or
            opening a bank account.
          </p>
          <p>
            Small groups can be organised on request — ideal for corporate
            teams or friendship pairs learning together.
          </p>
        </>
      ),
    },
    ch4: {
      title: "From the screen to the world: immersion.",
      beforeLoop: (
        <>
          <p>
            Even the best online lessons have a limit: the screen itself. To
            truly accelerate fluency, you need to live in the language. That is
            why we offer week-long <em>Spanish + Learn to Row</em> immersion
            programs in Galicia, Spain.
          </p>
          <p>
            Here, the communicative principle reaches its logical conclusion.
            You do not practise rowing and practise Spanish. You learn rowing{" "}
            <em>through</em> Spanish. All coaching from our partner rowing club
            (Tui, Galicia) is delivered in Spanish. Your dedicated teacher is
            present on the water to clarify when needed, but more importantly,
            to observe. They note gaps, hesitations, and errors that become the
            raw material for your next formal lesson.
          </p>
        </>
      ),
      loop: [
        { label: "Real activity", body: "Learning to row as a Masters-level adult." },
        { label: "Communicative need", body: "Ask for an oar adjustment; understand a technique correction." },
        { label: "Targeted lesson", body: "The specific vocabulary and structures you actually needed — not a textbook unit on \"sports\"." },
      ],
      afterLoop: (
        <p>
          The result is a week of <strong>20 hours of Spanish tuition</strong>{" "}
          plus <strong>20 hours of rowing practice</strong>, plus optional
          daily cultural activities — from local markets to historic visits.
          And because we believe language and culture are inseparable, those
          cultural moments generate further questions, further communicative
          needs, and further learning.
        </p>
      ),
    },
    ch5: {
      title: "Who we are for.",
      body: (
        <>
          <p>
            You are an adult. You are no longer in formal education, but you
            are still curious, still ambitious, still unwilling to settle for
            "good enough." You may be a corporate client needing Spanish for
            negotiations, or a professional in your 30s, 40s, or 50s who has
            felt the sting of stalled progress.
          </p>
          <p>
            You have tried the classroom (too rigid). You have tried the apps
            (too shallow). You have even tried well-meaning tutors on generic
            video calls (too limited).
          </p>
          <p>
            We are not a toy. We are not a textbook on a screen. We are a
            bridge between deliberate, structured teaching and the messy,
            glorious real world where language actually lives.
          </p>
        </>
      ),
    },
    closing: (
      <>
        Learn Spanish by doing. Communicate from day one. And when you are
        ready,{" "}
        <span className="italic">come row with us on the Miño river.</span>
      </>
    ),
    bookCta: "Book a lesson",
  },

  es: {
    eyebrow: "Nuestra filosofía de enseñanza",
    chapterLabel: "Capítulo",
    stepLabel: "Paso",
    headline: "Más allá de la app, más allá del aula.",
    lead: (
      <>
        Seguramente ya has intentado aprender un idioma antes. Quizás en una
        clase presencial, lidiando con el tráfico y un horario fijo, solo para
        descubrir que el libro de texto tenía poco que ver con una conversación
        real en una cafetería de Madrid. O quizás has probado las aplicaciones
        —cómodas, sin duda—, pero que al final se quedan en una descarga de
        dopamina a base de tarjetas de memoria y frases sueltas que rara vez
        sobreviven a un encuentro en el mundo real.
      </>
    ),
    pullQuote: "Esa frustración tiene una explicación. Y no eres tú.",
    afterPullQuote: (
      <>
        La mayoría de los métodos fracasan porque confunden el mapa con el
        territorio. Tratan el español como una asignatura que hay que
        memorizar —como las matemáticas o la notación musical—, cuando en
        realidad es una herramienta para usar. Las lenguas humanas no son
        forma, sino función. Uno no habla para conjugar perfectamente; habla
        para lograr un objetivo: cerrar un trato, hacer un amigo, moverse por
        una ciudad desconocida.
      </>
    ),
    ch1: {
      title: "El principio comunicativo.",
      body: (
        <>
          <p>
            Nuestro Director proviene de la tradición de la{" "}
            <em>Enseñanza Comunicativa de la Lengua</em> (CLT, por sus siglas
            en inglés). En la práctica, esto se traduce en una regla
            irrenunciable: el idioma nunca se enseña en el vacío. Cada
            lección, cada ejercicio, cada corrección se basa en comunicación
            real.
          </p>
          <p>
            No empezamos con tablas de gramática. Empezamos con una necesidad
            —un vacío comunicativo auténtico— y la gramática surge de forma
            natural como la herramienta para llenarlo. Ese «gancho» cognitivo
            es lo que hace que el aprendizaje sea memorable. Es la diferencia
            entre estudiar las reglas de la natación y ser lanzado al agua.
          </p>
        </>
      ),
    },
    ch2: {
      title: "Por qué nuestras aulas virtuales son diferentes.",
      intro:
        "Hay muchos profesores particulares que usan Zoom o Skype. Son buenos profesores, pero están limitados por herramientas genéricas que no están diseñadas para la pedagogía. Nosotros construimos nuestro propio entorno virtual de aprendizaje desde cero.",
      bullets: [
        "Compartir pantalla, videoconferencia, chat y pizarra virtual, todo integrado.",
        "Las grabaciones de las sesiones están disponibles automáticamente para los alumnos.",
        "Tú controlas el proceso. No hay aplicaciones de terceros. Tus datos no salen de nuestro ecosistema.",
      ],
      outro:
        "Esto nos permite recrear el dinamismo de un aula comunicativa presencial —tareas en pareja, retroalimentación en tiempo real, pizarras digitales compartidas— sin las limitaciones de los desplazamientos o los horarios fijos. Y a diferencia de una app, nuestras clases ocurren entre seres humanos negociando significado en tiempo real.",
    },
    ch3: {
      title: "Clases individuales (y grupos reducidos).",
      body: (
        <>
          <p>
            La mayoría de nuestras lecciones son individuales. ¿Por qué? Porque
            la comunicación no es un monólogo unidireccional, sino un diálogo.
            Un entorno privado nos permite adaptar cada situación comunicativa
            a tu contexto profesional o personal. Si eres abogado, negociamos
            un contrato. Si trabajas en logística, resolvemos una demora en una
            entrega. Si te preparas para mudarte a España, practicamos cómo
            llamar a un fontanero o abrir una cuenta bancaria.
          </p>
          <p>
            Los grupos reducidos se pueden organizar bajo petición —ideal para
            equipos corporativos o parejas de amigos que aprenden juntos.
          </p>
        </>
      ),
    },
    ch4: {
      title: "De la pantalla al mundo: inmersión.",
      beforeLoop: (
        <>
          <p>
            Incluso las mejores clases en línea tienen un límite: la propia
            pantalla. Para trabajar de verdad la fluidez, necesitas vivir en
            el idioma. Por eso ofrecemos programas de inmersión{" "}
            <em>Español + Remo</em> de una semana en Galicia, España.
          </p>
          <p>
            Aquí el principio comunicativo llega a su conclusión lógica. No
            practicas remo y aparte practicas español. Aprendes a remar{" "}
            <em>a través</em> del español. Todos los entrenamientos en nuestro
            club colaborador (Tui, Galicia) suceden en español. Tu profesor
            estará presente en el agua para aclarar dudas cuando sea
            necesario, pero sobre todo para observar. Toma nota de fallos,
            dudas y errores, que se convertirán en material para tu siguiente
            clase formal.
          </p>
        </>
      ),
      loop: [
        { label: "Actividad real", body: "Aprender remo como adulto." },
        { label: "Necesidad comunicativa", body: "Pedir un ajuste; entender una corrección técnica." },
        { label: "Clase dirigida", body: "El vocabulario y las estructuras que realmente necesitaste — no un tema de libro de texto sobre «deportes»." },
      ],
      afterLoop: (
        <p>
          El resultado es una semana con{" "}
          <strong>20 horas de clase de español</strong> más{" "}
          <strong>20 horas de práctica de remo</strong>, además de
          actividades culturales opcionales diarias —desde mercados locales
          hasta visitas históricas. Y como creemos que el idioma y la cultura
          son inseparables, esos momentos culturales generan nuevas preguntas,
          nuevas necesidades comunicativas y más aprendizaje.
        </p>
      ),
    },
    ch5: {
      title: "Para quién trabajamos.",
      body: (
        <>
          <p>
            Eres un adulto. Ya no estás en el sistema educativo formal, pero
            sigues sintiendo curiosidad, ambición y te niegas a conformarte
            con «bastante bien». Puedes ser un cliente corporativo que necesita
            español en sus negociaciones, o un profesional de 30, 40 o 50 años
            que siente que se ha estancado.
          </p>
          <p>
            Has probado con clases presenciales (demasiado rígidas). Has
            probado con apps (demasiado superficiales). Incluso has probado
            profesores particulares con videollamadas genéricas (demasiado
            limitados).
          </p>
          <p>
            No somos un juguete. No somos un libro de texto en una pantalla.
            Somos un puente entre la enseñanza estructurada y el mundo real,
            maravillosamente desordenado, donde la lengua realmente vive.
          </p>
        </>
      ),
    },
    closing: (
      <>
        Aprende español haciendo cosas. Comunica desde el primer día. Y cuando
        estés listo,{" "}
        <span className="italic">ven a remar con nosotros en el río Miño.</span>
      </>
    ),
    bookCta: "Reservar una clase",
  },
};
